import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAutocompleteSuggestions } from '../services/api/ElasticsearchService';
import { normalizeSpaces } from '../utils/common';

/**
 * `useAutocompleteSuggestions` の入力パラメータ。
 *
 * @typedef {Object} UseAutocompleteSuggestionsParams
 * @property {string} searchQuery 検索入力値
 * @property {string} documentType 文書種別コード
 * @property {'life'|'sonpo'|string} activeTab 現在のタブ
 * @property {boolean} isSearchDisabled 検索機能の無効状態
 * @property {(value: string) => void} onQueryChange 検索文字列変更時コールバック
 * @property {() => void} onSearch 検索実行コールバック
 * @property {boolean} isLoading 検索中フラグ
 */

/**
 * `useAutocompleteSuggestions` の戻り値。
 *
 * @typedef {Object} UseAutocompleteSuggestionsResult
 * @property {string[]} suggestions サジェスト候補
 * @property {boolean} isSuggestionLoading サジェスト取得中フラグ
 * @property {boolean} isSuggestionOpen 候補リストの表示状態
 * @property {number} activeIndex キーボード選択中の候補インデックス
 * @property {boolean} shouldUseAutocomplete サジェスト機能の有効状態
 * @property {(index: number | ((prev: number) => number)) => void} setActiveIndex 選択中インデックス設定関数
 * @property {() => void} closeSuggestions サジェストを閉じるハンドラー
 * @property {(suggestion: string) => void} handleSuggestionSelect 候補選択ハンドラー
 * @property {(event: React.ChangeEvent<HTMLInputElement>) => void} handleInputChange 入力変更ハンドラー
 * @property {() => void} handleCompositionStart IME開始ハンドラー
 * @property {(event: React.CompositionEvent<HTMLInputElement>) => void} handleCompositionEnd IME終了ハンドラー
 * @property {() => void} handleInputFocus 入力フォーカスハンドラー
 * @property {() => void} handleMiniSearchClick 検索ボタンクリックハンドラー
 * @property {(event: React.KeyboardEvent<HTMLInputElement>) => void} handleKeyDown キーボード操作ハンドラー
 */

/**
 * Elasticsearchサジェストの取得状態と、入力UIの操作ハンドラーをまとめて管理する。
 *
 * @param {UseAutocompleteSuggestionsParams} params フック入力
 * @returns {UseAutocompleteSuggestionsResult} サジェスト状態と各種ハンドラー
 */
export const useAutocompleteSuggestions = ({
  searchQuery,
  documentType,
  activeTab,
  isSearchDisabled,
  onQueryChange,
  onSearch,
  isLoading,
  closeSignal = 0,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionLoading, setIsSuggestionLoading] = useState(false);
  const [isSuggestionOpen, setIsSuggestionOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [isComposing, setIsComposing] = useState(false);

  // レスポンスの前後逆転を防ぐため、最新リクエストIDを保持する。
  const latestRequestIdRef = useRef(0);
  const debounceTimerRef = useRef(null);
  const previousAutocompleteScopeRef = useRef({
    documentType,
    activeTab,
  });

  // 文書種別・タブ変更時にサジェストを強制再取得するためのトリガー
  const [scopeChangeTrigger, setScopeChangeTrigger] = useState(0);

  useEffect(() => {
    setScopeChangeTrigger((prev) => prev + 1);
  }, [documentType, activeTab]);

  const shouldUseAutocomplete = Boolean(documentType) && !isSearchDisabled;

  useEffect(() => {
    // IME変換中、またはサジェスト無効時は候補表示をリセットする。
    if (!shouldUseAutocomplete || isComposing) {
      setSuggestions([]);
      setIsSuggestionOpen(false);
      setIsSuggestionLoading(false);
      setActiveIndex(-1);
      return;
    }

    const normalizedQuery = normalizeSpaces(searchQuery).trim();
    // 空文字・100文字超はバックエンド制約に合わせて問い合わせしない。

    const hasScopeChanged =
      previousAutocompleteScopeRef.current.documentType !== documentType ||
      previousAutocompleteScopeRef.current.activeTab !== activeTab;

    previousAutocompleteScopeRef.current = { documentType, activeTab };

    if (!normalizedQuery || normalizedQuery.length > 100) {
      setSuggestions([]);
      setIsSuggestionOpen(false);
      setIsSuggestionLoading(false);
      setActiveIndex(-1);
      return;
    }

    // 新しい入力が来たらIDを更新し、古いレスポンスを無視できるようにする。
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    if (hasScopeChanged) {
      setSuggestions([]);
      setIsSuggestionOpen(false);
      setActiveIndex(-1);
    }

    setIsSuggestionLoading(true);

    // 連続入力時のAPI呼び出しを減らすため、300msデバウンスする。
    debounceTimerRef.current = setTimeout(async () => {
      try {
        const nextSuggestions = await fetchAutocompleteSuggestions(normalizedQuery, documentType, activeTab, 5);

        // 古いリクエストの結果を破棄するため、リクエストIDで最新性を確認する（競合状態防止）
        if (latestRequestIdRef.current !== requestId) {
          return;
        }

        setSuggestions(nextSuggestions);
        setIsSuggestionOpen(nextSuggestions.length > 0);
        setActiveIndex(-1);
      } catch {
        if (latestRequestIdRef.current !== requestId) {
          return;
        }
        // 補完APIの失敗時は候補のみ閉じ、検索本体は継続可能にする
        setSuggestions([]);
        setIsSuggestionOpen(false);
        setActiveIndex(-1);
      } finally {
        if (latestRequestIdRef.current === requestId) {
          setIsSuggestionLoading(false);
        }
      }
    }, hasScopeChanged ? 0 : 300);

    return () => {
      clearTimeout(debounceTimerRef.current);
    };
  }, [searchQuery, shouldUseAutocomplete, isComposing, documentType, activeTab, scopeChangeTrigger]);

  /**
   * 候補を確定し、候補リストを閉じる。
   *
   * @param {string} suggestion 選択された候補文字列
   */
  const handleSuggestionSelect = (suggestion) => {
    onQueryChange(normalizeSpaces(suggestion));
    setIsSuggestionOpen(false);
    setActiveIndex(-1);
  };

  const closeSuggestions = useCallback(() => {
    // 進行中のデバウンスタイマーをキャンセルし、古いレスポンスで再オープンされないようにする
    clearTimeout(debounceTimerRef.current);
    // リクエストIDを更新して、飛行中のAPIレスポンスを無効化する
    latestRequestIdRef.current += 1;
    setIsSuggestionLoading(false);
    setIsSuggestionOpen(false);
    setActiveIndex(-1);
  }, []);

  useEffect(() => {
    if (closeSignal > 0) {
      closeSuggestions();
    }
  }, [closeSignal, closeSuggestions]);

  /**
   * 入力値更新時に検索文字列を反映し、空入力なら候補をクリアする。
   *
   * @param {React.ChangeEvent<HTMLInputElement>} event 入力イベント
   */
  const handleInputChange = (event) => {
    const value = event.target.value;
    onQueryChange(value);

    if (!value.trim()) {
      setSuggestions([]);
      setIsSuggestionOpen(false);
      setActiveIndex(-1);
    }
  };

  /**
   * IME変換開始時に未確定文字でのサジェスト取得を停止する。
   */
  const handleCompositionStart = () => {
    // IME変換中は未確定文字でのサジェスト取得を止める。
    setIsComposing(true);
  };

  /**
   * IME変換確定後にサジェスト取得を再開する。
   *
   * @param {React.CompositionEvent<HTMLInputElement>} event IMEイベント
   */
  const handleCompositionEnd = (event) => {
    // 変換確定後の文字列で正規化し、サジェスト取得を再開する。
    setIsComposing(false);
    onQueryChange(event.target.value);
  };

  /**
   * フォーカス時に候補が存在すればリストを開く。
   */
  const handleInputFocus = () => {
    if (shouldUseAutocomplete && suggestions.length > 0) {
      setIsSuggestionOpen(true);
    }
  };

  /**
   * ミニ検索ボタン押下時に候補を閉じて検索を実行する。
   */
  const handleMiniSearchClick = () => {
    if (isLoading || isSearchDisabled) {
      return;
    }
    setIsSuggestionOpen(false);
    onSearch();
  };

  /**
   * 候補の上下移動・確定・検索実行をキーボード操作で処理する。
   *
   * @param {React.KeyboardEvent<HTMLInputElement>} e キーボードイベント
   */
  const handleKeyDown = (e) => {
    // ↓ キー: 候補が開いている場合、次の候補にフォーカスを移動する
    if (e.key === 'ArrowDown' && isSuggestionOpen && suggestions.length > 0) {
      e.preventDefault();
      setActiveIndex((prev) => (prev + 1) % suggestions.length);
      return;
    }

    // ↑ キー: 候補が開いている場合、前の候補にフォーカスを移動する
    if (e.key === 'ArrowUp' && isSuggestionOpen && suggestions.length > 0) {
      e.preventDefault();
      setActiveIndex((prev) => (prev <= 0 ? suggestions.length - 1 : prev - 1));
      return;
    }

    // Escape キー: 候補一覧を閉じ、フォーカスをリセットする
    if (e.key === 'Escape' && isSuggestionOpen) {
      setIsSuggestionOpen(false);
      setActiveIndex(-1);
      return;
    }

    if (e.key === 'Enter' && !isLoading && !isSearchDisabled) {
      // Enter キー（候補選択中）: フォーカス中の候補を検索ワードに確定する
      if (isSuggestionOpen && activeIndex >= 0 && suggestions[activeIndex]) {
        e.preventDefault();
        handleSuggestionSelect(suggestions[activeIndex]);
        return;
      }
      // Enter キー（候補未選択）: 候補一覧を閉じ、検索を実行する
      setIsSuggestionOpen(false);
      onSearch();
    }
  };

  return {
    suggestions,
    isSuggestionLoading,
    isSuggestionOpen,
    activeIndex,
    shouldUseAutocomplete,
    setActiveIndex,
    closeSuggestions,
    handleSuggestionSelect,
    handleInputChange,
    handleCompositionStart,
    handleCompositionEnd,
    handleInputFocus,
    handleMiniSearchClick,
    handleKeyDown,
  };
};
